import { createTheme } from '@mui/material/styles';
import { remCalc } from 'utils/remCalc';
import { getColor } from 'utils/baseTheme';
import breakpoints from 'utils/breakpoints';

import star from 'images/icons/star.svg';
import star_blue700 from 'images/icons/star_blue700.svg';
import chevron from 'images/chevron.svg';

const theme = createTheme({
	breakpoints: breakpoints,
});

const styles = {
	components: {
		MuiBackdrop: {
			styleOverrides: {
				root: {
					'&.Timeline-backdrop': {
						background: 'rgba(0, 0, 0, .3)',
						zIndex: 1300,
					}
				}
			}
		},

		MuiPaper: {
			styleOverrides: {
				root: {
					'&.Timeline-yearSelectorList': {
						background: getColor('yellow', 600),
						color: getColor('blue', 700),
						// overflow: 'auto',
						scrollbarWidth: 'thin',
						scrollbarColor: `rgba(0, 0, 0, .3) rgba(196, 196, 196, 0)`,
						zIndex: 1500,
						overscrollBehavior: 'contain',
						
						/* These styles are for when it is opened directly below */
						border: 'none',
						boxShadow: 'none',
						borderTopLeftRadius: 0,
						borderTopRightRadius: 0,
						borderBottomLeftRadius: remCalc(11),
						borderBottomRightRadius: remCalc(11),
						marginTop: '-8px',

						'.MuiListSubheader-root': {
							background: getColor('yellow', 600),
							color: getColor('red', 500),
							fontFamily: [
								'franklin-gothic-atf',
								'sans-serif',
							].join(', '),
							fontSize: remCalc(16),
							fontWeight: 600,
							display: 'flex',
							alignItems: 'center',
							gap: '8px',

							'&:after': {
								content: '""',
								background: getColor('red', 500),
								height: '3px',
								width: '100%',
							}
						},

						'.MuiMenuItem-root': {
							fontFamily: [
								'franklin-gothic-atf',
								'sans-serif',
							].join(', '),
							fontWeight: 600,
							fontSize: remCalc(28),
							color: getColor('blue', 700),
						},
					},
				}
			}
		},

		MuiContainer: {
			styleOverrides: {
				root: {
					'&.Timeline-root': {
						padding: remCalc(30),
						[theme.breakpoints.up('ms')]: {
							padding: remCalc(78),
						},
					},

					'.Timeline-mobile': {
						padding: remCalc('60 0 0 0'),
						[theme.breakpoints.up('ms')]: {
							display: 'none',
						}
					},

					'.Timeline-desktop': {
						display: 'none',
						[theme.breakpoints.up('ms')]: {
							display: 'block',
						}
					},

					'.Timeline-headline': {
						textAlign: 'center',
						fontFamily: [
							'franklin-gothic-atf',
							'sans-serif',
						].join(', '),
						fontWeight: 400,
						fontSize: remCalc(40),
						[theme.breakpoints.up('ms')]: {
							fontSize: remCalc(65),
						},
						textTransform: 'uppercase',
						marginBottom: remCalc(12),
						strong: {
							fontWeight: 600,
						}
					},

					'.Timeline-yearSelect': {
						width: '100%',
						backgroundColor: getColor('yellow', 600),
						color: getColor('blue', 700),
						fontSize: remCalc(40),
						fontFamily: [
							'franklin-gothic-atf',
							'sans-serif',
						].join(', '),
						fontWeight: 600,
						border: 'none',
						borderRadius: remCalc(11),
						scrollMarginTop: remCalc(50),
						transition: 'all 300ms',

						'&.isOpen': {
							borderBottomRightRadius: 10,
							borderBottomLeftRadius: 10,
						},

						fieldset: {
							border: 'none',
						},

						'.MuiSelect-select': {
							backgroundImage: `url('${star_blue700}')`,
							backgroundRepeat: 'no-repeat',
							backgroundPosition: 'left 20px center',
							padding: remCalc('3px 20px 3px 66px'),
						},

						'.MuiSelect-icon': {
							top: 'unset',
							right: remCalc(20),

						},
						// position: 'relative',
						// zIndex: 1500,

					},

					'.Timeline-topContainer': {
						padding: '50px 0 0 0',
					},

					'.Timeline-entries': {
						[theme.breakpoints.up('ms')]: {
							position: 'relative',
							listStyle: 'none',
							margin: 0,
							padding: '0 20px 0 0',
							zIndex: 2,
							marginRight: '-30px',
							'@supports not selector(::-webkit-scrollbar)': {
								scrollbarWidth: 'thin',
								scrollbarColor: `rgba(188, 204, 208, .2) rgba(196, 196, 196, 0)`,
							},
							maxHeight: remCalc(521),
							overflowY: 'auto',
							transition: 'all 300ms',
							overscrollBehavior: 'contain',

							'&:hover': {
								'@supports not selector(::-webkit-scrollbar)': {
									scrollbarColor: `${getColor('gray', 500)} rgba(196, 196, 196, 0)`,
								},
								'&::-webkit-scrollbar-thumb': {
									background: getColor('gray', 500),
								},
							},

							'&::-webkit-scrollbar': {
								'WebkitAppearance': 'none',
								width: remCalc(6),
								background: 'rgba(188, 204, 208, .1)',
								transition: 'all 300ms',
							},
							'&::-webkit-scrollbar-thumb': {
								background: 'rgba(188, 204, 208, .5)',
								borderRadius: '5px',
								transition: 'all 300ms',
							},
						},

						'.Timeline-entry': {
							fontSize: remCalc(16),
							paddingLeft: remCalc(20),
							marginBottom: remCalc(32),
							borderLeft: `3px solid ${getColor('yellow', 600)}`,
							cursor: 'default',

							'&:hover': {
								'.Timeline-entryLinkContainer': {
									[theme.breakpoints.up('ms')]: {
										gridTemplateRows: '40px',
										overflow: 'visible',

										'.Timeline-entryLink': {
											opacity: 1,
										},
									},
								}
							},

							'.Timeline-entryLinkContainer': {
								[theme.breakpoints.up('ms')]: {
									// display: 'grid',
									// gridTemplateRows: 0,
									transition: 'all 1s',
									// overflow: 'hidden',
								},
							},

							'.Timeline-entryLink': {
								display: 'block',
								fontSize: remCalc(16),
								textDecoration: 'none',
								textTransform: 'uppercase',
								marginTop: remCalc(16),
								fontFamily: [
									'din-2014',
									'sans-serif',
								].join(', '),
								fontWeight: 600,
								transition: 'all 300ms',
								[theme.breakpoints.up('ms')]: {
									// opacity: 0,
								},

								'&:after': {
									content: `url('${chevron}')`,
									display: 'inline-block',
									marginLeft: remCalc(20),
									verticalAlign: 'middle',
								},
							},
						},
					},

					'.Timeline-mobileSlideContainer': {
						padding: remCalc('32px 0 0 0'),
						'.Image-pictureContainer': {
							height: 'auto',
						},
						'.Image-figure': {
							aspectRatio: 'unset',
						},
					},

					'.Timeline-yearTitle': {
						fontWeight: 600,
						fontFamily: [
							'franklin-gothic-atf',
							'sans-serif',
						].join(', '),
						marginBottom: remCalc(40),
					},

					'.Timeline-entryAssets': {
						[theme.breakpoints.up('ms')]: {
							listStyle: 'none',
							margin: 0,
							padding: '0 0 50px 50px',
							height: remCalc(571),
						},

						'.Timeline-entryAsset': {
							marginBottom: remCalc(32),

							[theme.breakpoints.up('ms')]: {
								marginBottom: 0,
								maxWidth: remCalc(757),
								display: 'none',
							},

							'&.Timeline-activeEntryAsset': {
								display: 'block',
							},

							'.Image-figure': {
								margin: 0,
								// height: remCalc(184),
								[theme.breakpoints.up('ms')]: {
									height: remCalc(521),
								},
							},
							'.Image-caption': {
								[theme.breakpoints.up('ms')]: {
									marginTop: '1rem',
									textAlign: 'right',
								},
								fontFamily: [
									'franklin-gothic-atf',
									'sans-serif',
								].join(', '),
								fontSize: remCalc(16),
							},

							video: {
								[theme.breakpoints.up('ms')]: {
									height: remCalc(521),
								},
							}
						},

					},

					'.Timeline-year': {
						fontSize: remCalc(16),
						fontWeight: 700,
						fontFamily: [
							'franklin-gothic-atf',
							'sans-serif',
						].join(', '),
						position: 'relative',
						cursor: 'hand',
						opacity: '0.3',
						transition: 'all 300ms',
						'&:before': {
							transition: 'all 300ms',
							opacity: 0,
							content: `url('${star}')`,
							position: 'absolute',
							width: remCalc(18),
							height: remCalc(18),
							transform: `translateY(-28px)`,
						},

						'&.Timeline-activeYear': {
							opacity: 1,
							'&:before': {
								opacity: 1,
							}
						}
					},

					'.Timeline-yearSliderContainer': {
						display: 'flex',
						'.Timeline-yearSliderInnerContainer': {
							flexGrow: 1,
							width: 1,
						},
						'.Timeline-yearSliderPrev, .Timeline-yearSliderNext': {
							width: remCalc(24),
							padding: 0,
							margin: 0,
							flexShrink: 0,
							flexGrow: 0,
							'&[aria-disabled="true"]': {
								opacity: '0.5',
							},
							transition: 'all 300ms',
						},
						'.Timeline-yearSliderNext': {
							transform: 'scaleX(-1)',
						},
					},

					'.Timeline-yearSlide': {
						padding: '20px 0',
					},

					'.Timeline-mobileSwiperPagination': {
						display: 'flex',
						gap: remCalc(16),
						justifyContent: 'center',
						'&.swiper-pagination-lock': {
							display: 'none',
						},

						'.swiper-pagination-bullet': {
							display: 'block',
							width: remCalc(20),
							height: remCalc(20),
							background: '#d9d9d9',
							borderRadius: '50%',
							opacity: 0.3,
							transition: 'all 300ms',

							'&.swiper-pagination-bullet-active': {
								opacity: 1,
							}
						}
					},
				},
			},
		},
	},

}

export default styles;